import React, { CSSProperties, useState } from 'react';
import { HbrButton, HbrSpinner } from '@harbor/elements-react-standalone';

interface LoginBoxProps {
  loading: boolean;
  onClick?: () => void;
  onCiscoSSOClick?: () => void;
  buttonText: string;
  ciscoButtonText?: string;
  loadingText?: string;
  subtitle?: string;
  showButton?: boolean;
  showCiscoOption?: boolean;
  // Stacking order of the centered card. Defaults to the login-screen value; the session-recovery
  // overlay raises it above its full-screen backdrop (and any lingering tooltip/popover layer).
  overlayZIndex?: number;
}

const overlayStyle = (zIndex: number): CSSProperties => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex,
  width: 'min(460px, calc(100vw - 32px))',
  pointerEvents: 'auto',
});

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '220px',
  padding: '32px 28px',
  backgroundColor: 'rgba(232, 230, 230, 0.96)',
  borderRadius: '16px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
  boxSizing: 'border-box',
  textAlign: 'center',
  backdropFilter: 'blur(4px)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  marginBottom: '8px',
  fontSize: '1.5rem',
  fontWeight: 600,
  color: '#1f2937',
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  marginBottom: '24px',
  fontSize: '0.95rem',
  color: '#4b5563',
};

const actionStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: '220px',
  minHeight: '40px',
};

const accordionWrapStyle: CSSProperties = {
  marginTop: '20px',
  width: '100%',
  textAlign: 'right',
};

const accordionHeaderStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#1f2937',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '6px',
  boxSizing: 'border-box',
};

const accordionContentStyle: CSSProperties = {
  marginTop: '10px',
  width: '100%',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  padding: '16px',
  background: '#ffffff',
  boxSizing: 'border-box',
  textAlign: 'left',
};

const accordionTextStyle: CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: '0.9rem',
  color: '#4b5563',
};

const LoginBox: React.FC<LoginBoxProps> = ({
                                             loading,
                                             onClick,
                                             onCiscoSSOClick,
                                             buttonText,
                                             ciscoButtonText = 'Login with Cisco SSO',
                                             loadingText = 'Logging in...',
                                             subtitle = 'Sign in to continue',
                                             showButton = true,
                                             showCiscoOption = true,
                                             overlayZIndex = 1000,
                                           }) => {
  const [showCiscoAccordion, setShowCiscoAccordion] = useState(false);

  return (
    <div style={overlayStyle(overlayZIndex)}>
      <div style={containerStyle}>
        <h2 style={titleStyle}>Security Hub</h2>
        <p style={subtitleStyle}>{subtitle}</p>

        <div style={actionStyle}>
          {loading ? (
            <HbrSpinner size="medium">
              <div slot="label">{loadingText}</div>
            </HbrSpinner>
          ) : (
            showButton && (
              <HbrButton onClick={onClick} disabled={loading}>
                {buttonText}
              </HbrButton>
            )
          )}
        </div>

        {!loading && showCiscoOption && (
          <div style={accordionWrapStyle}>
            <button
              type="button"
              style={accordionHeaderStyle}
              onClick={() => setShowCiscoAccordion((prev) => !prev)}
            >
              <span>Other sign-in </span>
              <span>{showCiscoAccordion ? '−' : '+'}</span>
            </button>

            {showCiscoAccordion && (
              <div style={accordionContentStyle}>
                <p style={accordionTextStyle}>
                  Use Cisco SSO if you need access without the Salesforce-connected login flow.
                </p>
                <HbrButton onClick={onCiscoSSOClick} disabled={loading}>
                  {ciscoButtonText}
                </HbrButton>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginBox;